import { Typography } from "components/ui/Typography";
import { Box } from "components/ui/box";
import { Card } from "components/ui/card";
import React from "react";

import { baseTemplates } from "templates/src/data";
import { BaseTemplate } from "templates/src/types";
import { PrimaryInput } from "components/input";
import { TemplateCategory } from "templates/src/types";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import { Button } from "components/ui/button";
import { colors } from "src/constants";
import { TemplateCard } from "./template-card";
import { LargeSectionHeading } from "modules/selections/common";

const CategoryTags = ({ selectedCategory, onFilterTemplatesByCategory }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        margin: "24px 0",
        rowGap: "6px",
      }}
    >
      {Object.values(TemplateCategory).map((category) => {
        const selected = selectedCategory === category;
        return (
          <Button
            key={category}
            size="small"
            value={category}
            sx={{
              border: "none",
              color: selected ? colors.textWhite1 : colors.black1,
              backgroundColor: selected ? `${colors.red1}!important` : "none",
              fontSize: "14px",
              marginRight: "3px",
              borderRadius: "4px",
              fontWeight: 500,
            }}
            selected={selected}
            onClick={() => onFilterTemplatesByCategory(category)}
          >
            {category}
          </Button>
        );
      })}
    </Box>
  );
};
const TemplateLists = ({
  templates,
  inEditor,
  onSelectTemplate,
  getNavigateProps,
}: {
  templates: BaseTemplate[];
  onSelectTemplate?: (id: string) => void;
  inEditor?: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: "12px",
        rowGap: "24px",
      }}
    >
      {templates.map((template) => {
        return (
          <TemplateCard
            slug={template.slug}
            key={template.slug}
            name={template.name}
            description={template.shortDescription}
            getNavigateProps={
              onSelectTemplate
                ? () => ({
                    onClick: () => {
                      onSelectTemplate?.(template.id);
                    },
                  })
                : getNavigateProps
            }
            sx={{
              flexBasis: inEditor
                ? "100%"
                : {
                    xs: "100%",
                    sm: `100%`,
                    md: `45%`,
                    lg: "45%",
                    xl: "24%",
                  },
            }}
          />
        );
      })}
    </Box>
  );
};

export const TemplatesContainer = ({
  rootProps = {},
  onSelectTemplate,
  inEditor = false,
  getNavigateProps,
  onServer = false,
  title,
}) => {
  const [value, setValue] = React.useState("");

  const [selectedCategory, setSelectedCategory] = React.useState(
    TemplateCategory.All
  );

  const templates = React.useMemo(() => {
    let filteredTemplates = [...baseTemplates];
    if (onServer) {
      filteredTemplates = filteredTemplates.filter((template) => {
        return template.slug !== "ai-article-writer";
      });
    }
    const categoryToLower = selectedCategory.toLowerCase();
    const valueToLower = value ? value.toLowerCase().trim() : "";
    if (selectedCategory !== TemplateCategory.All) {
      filteredTemplates = filteredTemplates.filter((template) => {
        return template.categories
          .map((category) => category.toLowerCase())
          .includes(categoryToLower);
      });
    }
    if (valueToLower) {
      filteredTemplates = filteredTemplates.filter((template) => {
        return (
          template.shortDescription.toLowerCase().includes(valueToLower) ||
          template.categories
            .map((category) => category.toLowerCase())
            .includes(valueToLower) ||
          template.name.toLowerCase().includes(valueToLower)
        );
      });
    }
    return filteredTemplates;
  }, [selectedCategory, value]);
  const onFilterTemplatesByCategory = React.useCallback((category) => {
    setSelectedCategory(category);
  }, []);
  const onFilterTemplatesByKeywords = React.useCallback((value: string) => {
    setValue(value);
  }, []);

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFilterTemplatesByKeywords(event.target.value);
  };

  return (
    <Box {...rootProps}>
      {inEditor || onServer ? null : (
        <LargeSectionHeading>{title || "AI Writing Tools"}</LargeSectionHeading>
      )}
      <PrimaryInput
        fullWidth
        value={value}
        onChange={onInputChange}
        placeholder={
          "What do you want to generate? Eg: LinkedIn post, email, etc."
        }
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlinedIcon />
            </InputAdornment>
          ),
        }}
      />
      <CategoryTags
        selectedCategory={selectedCategory}
        onFilterTemplatesByCategory={onFilterTemplatesByCategory}
      />
      <TemplateLists
        onSelectTemplate={onSelectTemplate}
        inEditor={inEditor}
        templates={templates}
        getNavigateProps={getNavigateProps}
      />
    </Box>
  );
};
